import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import {
  CheckCircle2, Clock, FileSignature, AlertTriangle, Phone,
  Mail, ShieldCheck, ExternalLink, Loader2, XCircle, RefreshCcw, Sparkles,
} from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Polling interval in millisec
const POLL_INTERVALS = {
  awaiting_admin_validation: 30000,
  sent_for_signature: 20000,
  identity_pending: 20000,
  ongoing: 20000,
  signed: 0,        // stop polling
  declined: 0,
  expired: 0,
  canceled: 0,
  error: 0,
};

export default function PraticaCreditoCompletataPage() {
  const { praticaId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!praticaId || !token) return;
    try {
      const { data: resp } = await axios.get(
        `${API}/credito/pratica/${encodeURIComponent(praticaId)}/signature-status`,
        { params: { token } }
      );
      setData(resp);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossibile recuperare lo stato della pratica.");
    } finally {
      setLoading(false);
    }
  }, [praticaId, token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Polling automatico
  useEffect(() => {
    if (!data) return;
    const status = data.firma_status === "not_sent"
      ? "awaiting_admin_validation"
      : data.firma_status;
    const interval = POLL_INTERVALS[status] ?? 0;
    if (interval <= 0) return;
    timerRef.current = setTimeout(fetchStatus, interval);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [data, fetchStatus]);

  const statusCategory = useMemo(() => {
    if (!data) return "loading";
    const fs = data.firma_status;
    if (fs === "not_sent") return "awaiting";
    if (["sent_for_signature", "identity_pending", "ongoing"].includes(fs)) return "ready";
    if (fs === "signed") return "signed";
    if (["declined", "expired", "canceled"].includes(fs)) return "failed";
    return "error";
  }, [data]);

  return (
    <>
      <Header />
      <main
        className="min-h-screen bg-gradient-to-b from-[#FCFCFA] via-[#F5F8F6] to-[#FCFCFA] pt-24 pb-20"
        data-testid="credito-completata-page"
      >
        <div className="max-w-3xl mx-auto px-5">
          {loading && !data && (
            <div className="text-center py-20">
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-[#163321] mb-4" />
              <p className="text-sm text-[#4A5E51]">Caricamento stato pratica...</p>
            </div>
          )}

          {!loading && error && (
            <ErrorPanel error={error} onRetry={fetchStatus} />
          )}

          {data && (
            <AnimatePresence mode="wait">
              {statusCategory === "awaiting" && (
                <AwaitingPanel key="awaiting" data={data} onRefresh={fetchStatus} />
              )}
              {statusCategory === "ready" && (
                <ReadyToSignPanel key="ready" data={data} praticaId={praticaId} onRefresh={fetchStatus} />
              )}
              {statusCategory === "signed" && (
                <SignedPanel key="signed" data={data} praticaId={praticaId} token={token} />
              )}
              {statusCategory === "failed" && (
                <FailedPanel key="failed" data={data} />
              )}
              {statusCategory === "error" && (
                <GenericErrorPanel key="gerr" data={data} />
              )}
            </AnimatePresence>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

// ================================ PANELS ================================

function AwaitingPanel({ data, onRefresh }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-white rounded-3xl shadow-xl border border-[#E5E2DB] p-8"
    >
      <div className="text-center mb-8">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          className="w-20 h-20 rounded-full bg-gradient-to-br from-[#163321] to-[#2d5f3e] flex items-center justify-center mx-auto mb-5"
        >
          <Clock className="w-10 h-10 text-[#D97B58]" />
        </motion.div>
        <h1 className="font-heading text-3xl text-[#0F1F14] font-semibold mb-2">
          Pratica inviata, in validazione
        </h1>
        <p className="text-sm text-[#4A5E51]">
          Riferimento <span className="font-mono font-bold tracking-wider text-[#163321]">{data.pratica_id}</span>
        </p>
      </div>

      <div className="bg-[#F5F8F6] border border-[#163321]/10 rounded-2xl p-5 mb-5">
        <p className="text-[#0F1F14] text-sm leading-relaxed">
          <span className="font-semibold">La tua pratica è stata ricevuta correttamente.</span> Il nostro staff la sta validando.
          Riceverai l'email con il link di firma entro le prossime <strong>ore lavorative</strong> (Lun-Ven 8:00-19:00).
        </p>
      </div>

      <Timeline steps={[
        { label: "Pratica inviata", done: true },
        { label: "Validazione staff Colucci Assicuratori", done: false, active: true },
        { label: "Email YouSign con link firma", done: false },
        { label: "Firma elettronica (AES + OTP SMS)", done: false },
        { label: "Proposta Ayvens ricevuta", done: false },
      ]} />

      <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-[#4A5E51]">
        <button onClick={onRefresh} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E2DB] hover:border-[#163321] transition-colors">
          <RefreshCcw className="w-3.5 h-3.5" /> Aggiorna stato
        </button>
        <span className="text-[#7C8A82]">Verrà aggiornato automaticamente ogni 30s</span>
      </div>

      <ContactStrip />
    </motion.div>
  );
}

function ReadyToSignPanel({ data, praticaId, onRefresh }) {
  const signUrl = data.sign_url;
  const hasUrl = !!signUrl;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-5"
    >
      <div className="bg-white rounded-3xl shadow-xl border border-[#E5E2DB] p-8">
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-[#D97B58] flex items-center justify-center mx-auto mb-4 shadow-lg">
            <FileSignature className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-3xl text-[#0F1F14] font-semibold mb-2">
            La tua pratica è pronta per la firma
          </h1>
          <p className="text-sm text-[#4A5E51]">
            Firma elettronicamente i documenti Ayvens in modo sicuro tramite YouSign
          </p>
        </div>

        {data.fallback_aes_standard && (
          <div className="mb-5 flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              Durante la firma caricherai la carta d'identità direttamente nell'app YouSign. Assicurati di acquisire <strong>sia il fronte che il retro</strong> del documento.
            </p>
          </div>
        )}

        {hasUrl && (
          <div className="grid md:grid-cols-2 gap-5 items-center">
            {/* Pulsante firma */}
            <div className="space-y-3">
              <a
                href={signUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-6 py-4 rounded-2xl bg-[#163321] text-white font-semibold shadow-lg hover:bg-[#0F1F14] transition-all text-sm"
                data-testid="btn-firma-yousign"
              >
                <FileSignature className="w-5 h-5 text-[#D97B58]" /> Firma ora su YouSign
                <ExternalLink className="w-4 h-4 opacity-60" />
              </a>
              <p className="text-[11px] text-center text-[#7C8A82] leading-relaxed">
                Si aprirà la pagina sicura YouSign per completare la firma.
              </p>
              <div className="h-px bg-[#E5E2DB]" />
              <div className="flex items-start gap-2 text-xs text-[#4A5E51]">
                <Mail className="w-4 h-4 text-[#D97B58] shrink-0 mt-0.5" />
                <div>
                  <span className="text-[#0F1F14] font-semibold">Abbiamo inviato anche l'email</span> a
                  {" "}<span className="font-mono text-[#163321]">{data.email_masked}</span>. Controlla pure in spam/promozioni.
                </div>
              </div>
              <div className="flex items-start gap-2 text-xs text-[#4A5E51]">
                <Phone className="w-4 h-4 text-[#D97B58] shrink-0 mt-0.5" />
                <div>
                  Durante la firma riceverai un <span className="text-[#0F1F14] font-semibold">SMS con codice OTP</span> su
                  {" "}<span className="font-mono text-[#163321]">{data.phone_masked}</span>.
                </div>
              </div>
            </div>

            {/* QR code */}
            <div className="bg-[#F5F8F6] rounded-2xl p-5 flex flex-col items-center">
              <div className="bg-white rounded-xl p-3 shadow-sm">
                <QRCodeSVG value={signUrl} size={180} bgColor="#FFFFFF" fgColor="#0F1F14" includeMargin={false} />
              </div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#4A5E51] mt-3">QR code firma</p>
              <p className="text-[11px] text-[#4A5E51] text-center mt-1">
                Inquadra per aprire il link sul tuo smartphone
              </p>
            </div>
          </div>
        )}

        {!hasUrl && (
          <div className="px-4 py-6 rounded-xl bg-amber-50 border border-amber-200 text-center">
            <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
            <p className="text-sm text-amber-800">
              Link di firma non ancora disponibile. Aspetta qualche secondo e aggiorna la pagina.
            </p>
            <button onClick={onRefresh} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-600 text-white text-xs font-semibold">
              <RefreshCcw className="w-3.5 h-3.5" /> Aggiorna
            </button>
          </div>
        )}
      </div>

      {/* Cosa serve per la firma */}
      <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6">
        <h3 className="font-heading text-lg text-[#0F1F14] font-semibold mb-3 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#D97B58]" /> Cosa ti serve per firmare
        </h3>
        <ul className="space-y-2 text-sm text-[#4A5E51]">
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#163321] shrink-0 mt-0.5" /> La tua <strong className="text-[#0F1F14]">Carta d'Identità</strong> (fronte + retro) o Passaporto</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#163321] shrink-0 mt-0.5" /> Il tuo cellulare per ricevere l'<strong className="text-[#0F1F14]">SMS OTP</strong></li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#163321] shrink-0 mt-0.5" /> Circa <strong className="text-[#0F1F14]">5 minuti</strong> di tempo</li>
        </ul>
      </div>

      <ContactStrip />
    </motion.div>
  );
}

function SignedPanel({ data, praticaId, token }) {
  const downloadUrl = `${API}/credito/pratica/${encodeURIComponent(praticaId)}/signed-pdf?token=${encodeURIComponent(token)}`;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="bg-white rounded-3xl shadow-xl border border-[#163321]/20 p-8 relative overflow-hidden"
    >
      {/* Confetti ornament */}
      <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-[#D97B58]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-[#163321]/5 blur-3xl pointer-events-none" />

      <div className="relative text-center mb-6">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
          className="w-24 h-24 rounded-full bg-gradient-to-br from-[#163321] to-[#2d5f3e] flex items-center justify-center mx-auto mb-5 shadow-2xl"
        >
          <CheckCircle2 className="w-12 h-12 text-white" />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="font-heading text-3xl sm:text-4xl text-[#0F1F14] font-semibold mb-2"
        >
          Firma completata! 🎉
        </motion.h1>
        <p className="text-sm text-[#4A5E51]">
          La tua pratica è firmata digitalmente con valore legale (AES eIDAS)
        </p>
      </div>

      <div className="bg-[#F5F8F6] border border-[#163321]/10 rounded-2xl p-5 mb-5">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <InfoRow label="Riferimento pratica" value={data.pratica_id} mono />
          <InfoRow label="Firmata il" value={formatDate(data.firma_signed_at)} />
          <InfoRow label="Livello firma" value="AES (Advanced Electronic Signature)" />
          <InfoRow label="Autenticazione" value="OTP SMS + verifica documento" />
        </div>
      </div>

      <div className="bg-white border border-[#D97B58]/30 rounded-2xl p-5 mb-5">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#D97B58] shrink-0 mt-0.5" />
          <div className="text-sm text-[#0F1F14] leading-relaxed">
            <p className="font-semibold mb-1">Prossimi passi</p>
            <p className="text-[#4A5E51]">
              Ti invieremo entro 24 ore il <strong className="text-[#0F1F14]">documento firmato</strong> e la
              {" "}<strong className="text-[#0F1F14]">proposta Ayvens</strong> completa sulla email fornita.
            </p>
          </div>
        </div>
      </div>

      {data.signed_pdf_available && (
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-2xl bg-[#163321] text-white font-semibold hover:bg-[#0F1F14] transition-colors text-sm"
          data-testid="btn-download-signed"
        >
          <FileSignature className="w-4 h-4 text-[#D97B58]" /> Scarica il PDF firmato
        </a>
      )}

      <ContactStrip compact />
    </motion.div>
  );
}

function FailedPanel({ data }) {
  const reason = {
    declined: "Hai rifiutato la firma",
    expired: "Il link di firma è scaduto",
    canceled: "La pratica è stata annullata",
  }[data.firma_status] || "La firma non è andata a buon fine";
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-white rounded-3xl shadow-lg border border-red-100 p-8"
    >
      <div className="text-center mb-6">
        <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <XCircle className="w-10 h-10 text-red-500" />
        </div>
        <h1 className="font-heading text-3xl text-[#0F1F14] font-semibold mb-2">{reason}</h1>
        <p className="text-sm text-[#4A5E51]">
          Riferimento pratica <span className="font-mono font-bold">{data.pratica_id}</span>
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6">
        <p className="text-sm text-amber-900">
          Nessun problema: contatta il nostro consulente per rinviare la firma o rivedere i dati.
        </p>
      </div>

      <a
        href="tel:0817611000"
        className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-2xl bg-[#163321] text-white font-semibold hover:bg-[#0F1F14] transition-colors text-sm"
      >
        <Phone className="w-4 h-4 text-[#D97B58]" /> Chiama il consulente — 081 761 1000
      </a>
    </motion.div>
  );
}

function GenericErrorPanel({ data }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-amber-50 border border-amber-200 rounded-3xl p-8 text-center"
    >
      <AlertTriangle className="w-12 h-12 text-amber-600 mx-auto mb-3" />
      <h1 className="font-heading text-2xl text-[#0F1F14] font-semibold mb-2">
        Problema tecnico
      </h1>
      <p className="text-sm text-amber-900 mb-4">
        {data.message || "Si è verificato un problema con la firma. Il nostro staff ti ricontatterà a breve."}
      </p>
      <a
        href="tel:0817611000"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#163321] text-white text-xs font-semibold"
      >
        <Phone className="w-4 h-4" /> 081 761 1000
      </a>
    </motion.div>
  );
}

function ErrorPanel({ error, onRetry }) {
  return (
    <div className="bg-white rounded-3xl border border-red-100 p-8 text-center">
      <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
      <h1 className="font-heading text-2xl text-[#0F1F14] font-semibold mb-2">Pratica non trovata</h1>
      <p className="text-sm text-[#4A5E51] mb-5">{error}</p>
      <div className="flex gap-3 justify-center">
        <button onClick={onRetry} className="px-4 py-2 rounded-full border border-[#E5E2DB] text-xs font-semibold hover:border-[#163321] inline-flex items-center gap-1.5">
          <RefreshCcw className="w-3.5 h-3.5" /> Riprova
        </button>
        <Link to="/" className="px-4 py-2 rounded-full bg-[#163321] text-white text-xs font-semibold">
          Torna alla Home
        </Link>
      </div>
    </div>
  );
}

// ================================ BUILDING BLOCKS ================================

function Timeline({ steps }) {
  return (
    <ol className="space-y-3">
      {steps.map((s, i) => (
        <li key={i} className="flex items-center gap-3">
          <span
            className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
              s.done
                ? "bg-[#163321] text-white"
                : s.active
                ? "bg-[#D97B58] text-white animate-pulse"
                : "bg-[#E5E2DB] text-[#7C8A82]"
            }`}
          >
            {s.done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
          </span>
          <span className={`text-sm ${s.done ? "text-[#0F1F14] font-medium" : s.active ? "text-[#163321] font-semibold" : "text-[#7C8A82]"}`}>
            {s.label}
          </span>
        </li>
      ))}
    </ol>
  );
}

function ContactStrip({ compact = false }) {
  return (
    <div className={`mt-${compact ? 5 : 7} flex flex-wrap items-center gap-2 justify-center text-xs text-[#4A5E51]`}>
      <span>Hai bisogno di aiuto?</span>
      <a href="tel:0817611000" className="inline-flex items-center gap-1 text-[#163321] font-semibold hover:underline">
        <Phone className="w-3.5 h-3.5" /> 081 761 1000
      </a>
      <span className="text-[#E5E2DB]">•</span>
      <a href="mailto:info@colucciassicuratori.com" className="inline-flex items-center gap-1 text-[#163321] font-semibold hover:underline">
        <Mail className="w-3.5 h-3.5" /> info@colucciassicuratori.com
      </a>
    </div>
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest font-bold text-[#7C8A82] mb-0.5">{label}</p>
      <p className={`text-sm text-[#0F1F14] font-medium ${mono ? "font-mono tracking-wider" : ""}`}>{value || "—"}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { dateStyle: "long", timeStyle: "short" });
  } catch {
    return iso;
  }
}
